// src/components/DailyAvgChartWithControls.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  BarChart,
  Bar,
} from 'recharts';
import api from '../utils/api';

// ===== 유틸 =====
function fmtDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function clampRange(s, e) {
  const sd = new Date(s);
  const ed = new Date(e);
  if (isNaN(sd) || isNaN(ed)) return { start: s, end: e };
  if (sd > ed) return { start: fmtDate(ed), end: fmtDate(sd) };
  return { start: s, end: e };
}

// ===== 네온 팔레트 & 공용 스타일 =====
const C = {
  bg: 'linear-gradient(180deg, rgba(0,10,25,.92), rgba(0,16,36,.96))',
  panelBorder: 'rgba(0,255,255,.22)',
  panelShadow: '0 12px 34px rgba(0,0,0,.45), 0 0 22px rgba(0,240,255,.08)',
  text: '#dff9ff',
  textDim: '#9feaff',
  accent: '#6ee7ff',
  accentSoft: 'rgba(0,255,255,.25)',
  grid: 'rgba(110,231,255,.25)',
  axis: '#baf6ff',
  danger: '#ff8b60',
  // 바 색상 (네온)
  barBlue: '#22d3ee',    // cyan
  barGreen: '#34d399',   // green
  barAmber: '#fbbf24',   // amber
};

const cardStyle = {
  height: 260,
  background: C.bg,
  border: `1px solid ${C.panelBorder}`,
  borderRadius: 12,
  padding: 10,
  boxShadow: C.panelShadow,
  color: C.text,
};

// 재사용 카드
function NeonCard({ title, children }) {
  return (
    <div style={cardStyle}>
      <h4
        style={{
          margin: '0 0 10px 6px',
          fontSize: 14,
          color: C.accent,
          textShadow: '0 0 8px rgba(0,255,255,.45)',
          letterSpacing: .2,
          fontWeight: 700,
        }}
      >
        {title}
      </h4>
      {children}
    </div>
  );
}

// 네온 버튼
const neonBtn = {
  padding: '8px 12px',
  borderRadius: 10,
  border: `1px solid ${C.accentSoft}`,
  background: 'rgba(0,25,55,.6)',
  color: C.text,
  fontWeight: 700,
  cursor: 'pointer',
  transition: 'transform .12s ease, box-shadow .2s ease, background .2s ease, border-color .2s ease',
};
const neonBtnHover = {
  background: 'rgba(0,32,72,.8)',
  borderColor: 'rgba(0,255,255,.6)',
  boxShadow: '0 0 12px rgba(0,255,255,.35)',
};

// 빠른 버튼
const quickBtn = {
  ...neonBtn,
  padding: '6px 10px',
  fontWeight: 600,
};

// 네온 인풋/라벨
const neonLabel = { fontSize: 13, color: C.textDim };
const neonInput = {
  padding: '6px 8px',
  borderRadius: 10,
  border: `1px solid ${C.accentSoft}`,
  background: 'rgba(0, 18, 36, .65)',
  color: C.text,
  outline: 'none',
  transition: 'box-shadow .2s ease, border-color .2s ease, background .2s ease',
};

// 툴팁/범례 공통 스타일
const tooltipStyle = {
  background: 'rgba(0,16,36,.96)',
  border: `1px solid ${C.accentSoft}`,
  borderRadius: 10,
  color: C.text,
  backdropFilter: 'blur(6px) saturate(130%)',
};
const legendStyle = { color: C.text, paddingTop: 4 };

// ===== 메인 컴포넌트 =====
export default function DailyAvgChartWithControls({ defaultDays = 30 }) {
  const today = useMemo(() => new Date(), []);
  const defaultStart = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() - (defaultDays - 1));
    return fmtDate(d);
  }, [today, defaultDays]);
  const defaultEnd = useMemo(() => fmtDate(today), [today]);

  const [start, setStart] = useState(defaultStart);
  const [end, setEnd] = useState(defaultEnd);

  const [{ loading, data, error }, setState] = useState({
    loading: false,
    data: [],
    error: null,
  });

  const load = async (s, e) => {
    const { start: S, end: E } = clampRange(s, e);
    setState((st) => ({ ...st, loading: true, error: null }));
    try {
      const res = await api.get('/daily-averages', { params: { start: S, end: E } });
      setState({ loading: false, data: Array.isArray(res.data) ? res.data : [], error: null });
    } catch (err) {
      console.error('통계 로드 실패', err);
      setState({ loading: false, data: [], error: err });
    }
  };

  useEffect(() => {
    load(start, end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setQuick = (days) => {
    const e = new Date();
    const s = new Date();
    s.setDate(e.getDate() - (days - 1));
    const S = fmtDate(s);
    const E = fmtDate(e);
    setStart(S);
    setEnd(E);
    load(S, E);
  };

  const apply = () => load(start, end);

  return (
    <div
      style={{
        width: '100%',
        display: 'grid',
        gap: 12,
        color: C.text,
      }}
    >
      {/* 컨트롤 바 (네온 글래스) */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 10,
          padding: 12,
          border: `1px solid ${C.panelBorder}`,
          borderRadius: 12,
          background: C.bg,
          boxShadow: C.panelShadow,
          backdropFilter: 'blur(6px) saturate(130%)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={neonLabel}>시작</label>
          <input
            type="date"
            value={start}
            max={end || undefined}
            onChange={(e) => setStart(e.target.value)}
            onFocus={(e) => (e.target.style.boxShadow = '0 0 12px rgba(0,255,255,.28)')}
            onBlur={(e) => (e.target.style.boxShadow = 'none')}
            style={neonInput}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={neonLabel}>종료</label>
          <input
            type="date"
            value={end}
            min={start || undefined}
            onChange={(e) => setEnd(e.target.value)}
            onFocus={(e) => (e.target.style.boxShadow = '0 0 12px rgba(0,255,255,.28)')}
            onBlur={(e) => (e.target.style.boxShadow = 'none')}
            style={neonInput}
          />
        </div>

        <button
          onClick={apply}
          onMouseEnter={(e) => Object.assign(e.currentTarget.style, neonBtnHover)}
          onMouseLeave={(e) => Object.assign(e.currentTarget.style, { background: neonBtn.background, borderColor: C.accentSoft, boxShadow: 'none' })}
          style={{ ...neonBtn, marginLeft: 4 }}
          title="선택한 기간으로 조회"
        >
          적용
        </button>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button
            onClick={() => setQuick(7)}
            onMouseEnter={(e) => Object.assign(e.currentTarget.style, neonBtnHover)}
            onMouseLeave={(e) => Object.assign(e.currentTarget.style, { background: quickBtn.background, borderColor: C.accentSoft, boxShadow: 'none' })}
            style={quickBtn}
          >
            최근 7일
          </button>
          <button
            onClick={() => setQuick(30)}
            onMouseEnter={(e) => Object.assign(e.currentTarget.style, neonBtnHover)}
            onMouseLeave={(e) => Object.assign(e.currentTarget.style, { background: quickBtn.background, borderColor: C.accentSoft, boxShadow: 'none' })}
            style={quickBtn}
          >
            최근 30일
          </button>
          <button
            onClick={() => setQuick(90)}
            onMouseEnter={(e) => Object.assign(e.currentTarget.style, neonBtnHover)}
            onMouseLeave={(e) => Object.assign(e.currentTarget.style, { background: quickBtn.background, borderColor: C.accentSoft, boxShadow: 'none' })}
            style={quickBtn}
          >
            최근 90일
          </button>
        </div>
      </div>

      {/* 차트 영역 */}
      <div style={{ width: '100%', display: 'grid', gap: 16 }}>
        {/* 온도 평균 */}
        <NeonCard title="온도 평균">
          {loading ? (
            <div style={{ padding: 16, color: C.textDim }}>로딩 중…</div>
          ) : error ? (
            <div style={{ padding: 16, color: C.danger }}>데이터를 불러오지 못했습니다.</div>
          ) : (data?.length ?? 0) === 0 ? (
            <div style={{ padding: 16, color: C.textDim }}>표시할 데이터가 없습니다.</div>
          ) : (
            <ResponsiveContainer>
              <BarChart data={data}>
                <CartesianGrid stroke={C.grid} strokeDasharray="3 3" />
                <XAxis dataKey="day" tick={{ fontSize: 12, fill: C.axis }} stroke={C.axis} />
                <YAxis tick={{ fontSize: 12, fill: C.axis }} stroke={C.axis} />
                <Tooltip contentStyle={tooltipStyle} itemStyle={{ color: C.text }} labelStyle={{ color: C.accent }} />
                <Legend wrapperStyle={legendStyle} iconType="circle" />
                <Bar dataKey="temperature" name="온도 평균" fill={C.barBlue} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </NeonCard>

        {/* 이동기준 연기 평균 */}
        <NeonCard title="이동기준 연기 평균">
          {loading ? (
            <div style={{ padding: 16, color: C.textDim }}>로딩 중…</div>
          ) : error ? (
            <div style={{ padding: 16, color: C.danger }}>데이터를 불러오지 못했습니다.</div>
          ) : (data?.length ?? 0) === 0 ? (
            <div style={{ padding: 16, color: C.textDim }}>표시할 데이터가 없습니다.</div>
          ) : (
            <ResponsiveContainer>
              <BarChart data={data}>
                <CartesianGrid stroke={C.grid} strokeDasharray="3 3" />
                <XAxis dataKey="day" tick={{ fontSize: 12, fill: C.axis }} stroke={C.axis} />
                <YAxis tick={{ fontSize: 12, fill: C.axis }} stroke={C.axis} />
                <Tooltip contentStyle={tooltipStyle} itemStyle={{ color: C.text }} labelStyle={{ color: C.accent }} />
                <Legend wrapperStyle={legendStyle} iconType="circle" />
                <Bar dataKey="movingStandardSmoke" name="이동기준 연기 평균" fill={C.barGreen} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </NeonCard>

        {/* 연기 평균값 */}
        <NeonCard title="연기 평균값">
          {loading ? (
            <div style={{ padding: 16, color: C.textDim }}>로딩 중…</div>
          ) : error ? (
            <div style={{ padding: 16, color: C.danger }}>데이터를 불러오지 못했습니다.</div>
          ) : (data?.length ?? 0) === 0 ? (
            <div style={{ padding: 16, color: C.textDim }}>표시할 데이터가 없습니다.</div>
          ) : (
            <ResponsiveContainer>
              <BarChart data={data}>
                <CartesianGrid stroke={C.grid} strokeDasharray="3 3" />
                <XAxis dataKey="day" tick={{ fontSize: 12, fill: C.axis }} stroke={C.axis} />
                <YAxis tick={{ fontSize: 12, fill: C.axis }} stroke={C.axis} />
                <Tooltip contentStyle={tooltipStyle} itemStyle={{ color: C.text }} labelStyle={{ color: C.accent }} />
                <Legend wrapperStyle={legendStyle} iconType="circle" />
                <Bar dataKey="smokeAvgValue" name="연기 평균값" fill={C.barAmber} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </NeonCard>
      </div>
    </div>
  );
}
